import { useState, useRef, useEffect, useMemo, KeyboardEvent } from 'react';
import { TextInput, Popover, Stack, Text, Group, Badge, ScrollArea, Button, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconFunction } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { componentsTestIds } from '@components/testids';
import {
  flattenVariablesRecord,
  formulaItemRefs,
  groupVariablesByKind,
  kindAwareComparator,
  missingItemRefs,
  type MaterializedVariable,
  type VariableComparator
} from '@bett3r-dev/teselly-rule-editor';

type MaterializedOption = {
  kind: string;
  label: string;
  value: string;
  description?: string;
};

type FormulaInputProps = {
  label: string;
  description?: string;
  value: string;
  onChange: ( value: string ) => void;
  variables?: Record<string, MaterializedOption[]>;
  required?: boolean;
  placeholder?: string;
  error?: string;
  onValidate?: ( value: string ) => string | null;
  /**
   * Optional set of itemIds present in the publication's current kit.
   * Passed in by `RuleConfigurationForm` for publication-bound rules.
   * When provided, formula chips that reference an itemId NOT in this set
   * surface a yellow warning marker. Saving is never blocked.
   */
  currentKitItemIds?: Set<string>;
};

type Suggestion = {
  value: string;
  label: string;
  description?: string;
  type: 'variable' | 'function' | 'operator';
  group?: string;
  originalValue?: string;
};

/**
 * Kebab-case a kind/source label for use in `data-testid` slugs.
 * Strips Latin diacritics so `Items en esta publicación` becomes
 * `items-en-esta-publicacion`. Handles camelCase (`channelCommissions`
 * → `channel-commissions`) by inserting a hyphen at case boundaries.
 */
const kebabCase = ( input: string ): string =>
  input
    .normalize( 'NFD' )
    .replace( /[̀-ͯ]/g, '' )
    .replace( /([a-z0-9])([A-Z])/g, '$1-$2' )
    .toLowerCase()
    .replace( /[^a-z0-9]+/g, '-' )
    .replace( /^-+|-+$/g, '' );

/**
 * Build the chip slug for a `data-testid="rule-editor-palette-chip-${slug}"`.
 * Prefers the `itemId` extracted from the chip's value token (for the
 * publication-bound `quantitiesByItem` / `priceListsByItem` /
 * `priceListsByQuantity` chips). Falls back to the kebab-cased
 * `source:` token when present, then to the kebab-cased `kind`/group.
 */
const buildChipSlug = ( suggestion: Suggestion ): string => {
  const token = suggestion.originalValue ?? suggestion.value;
  const itemIdMatch = /itemId:'([^']+)'/.exec( token );
  if ( itemIdMatch ) return itemIdMatch[ 1 ];
  const sourceMatch = /source:([A-Za-z0-9_]+)/.exec( token );
  if ( sourceMatch ) return kebabCase( sourceMatch[ 1 ]);
  if ( suggestion.group ) return kebabCase( suggestion.group );
  return kebabCase( suggestion.value );
};

export const FormulaInput = ({ label, description, value, onChange, variables = {}, required = false, placeholder = 'Enter formula', error, onValidate, currentKitItemIds }: FormulaInputProps ) => {
  const { t } = useTranslation( 'rules' );
  const { t: tAutomation } = useTranslation( 'automation' );
  const [ showSuggestions, setShowSuggestions ] = useState( false );
  const [ suggestions, setSuggestions ] = useState<Suggestion[]>([]);
  const [ selectedSuggestionIndex, setSelectedSuggestionIndex ] = useState( 0 );
  const inputRef = useRef<HTMLInputElement>( null );
  const [ displayValue, setDisplayValue ] = useState( '' );


  // Basic operators and functions
  const operators: Suggestion[] = [
    { value: '+', label: t( 'operator_addition', 'Suma (+)' ), type: 'operator' },
    { value: '-', label: t( 'operator_subtraction', 'Resta (-)' ), type: 'operator' },
    { value: '*', label: t( 'operator_multiplication', 'Multiplicación (*)' ), type: 'operator' },
    { value: '/', label: t( 'operator_division', 'División (/)' ), type: 'operator' }
  ];

  const functions: Suggestion[] = [
    { value: 'SUM()', label: t( 'function_sum', 'Función suma - SUM(valor1, valor2, ...)' ), type: 'function' },
    { value: 'AVG()', label: t( 'function_avg', 'Función promedio - AVG(valor1, valor2, ...)' ), type: 'function' },
    { value: 'COUNT()', label: t( 'function_count', 'Función contar - COUNT(valor1, valor2, ...)' ), type: 'function' },
    { value: 'MAX()', label: t( 'function_max', 'Función máximo - MAX(valor1, valor2, ...)' ), type: 'function' },
    { value: 'MIN()', label: t( 'function_min', 'Función mínimo - MIN(valor1, valor2, ...)' ), type: 'function' },
    { value: 'ROUND()', label: t( 'function_round', 'Función redondear - ROUND(valor, decimales)' ), type: 'function' },
    { value: 'ABS()', label: t( 'function_abs', 'Función valor absoluto - ABS(valor)' ), type: 'function' },
    { value: 'CEIL()', label: t( 'function_ceil', 'Función techo - CEIL(valor)' ), type: 'function' },
    { value: 'FLOOR()', label: t( 'function_floor', 'Función piso - FLOOR(valor)' ), type: 'function' },
    { value: 'SQRT()', label: t( 'function_sqrt', 'Función raíz cuadrada - SQRT(valor)' ), type: 'function' }
  ];

  const functionMappings: Record<string, string> = {
    sum: 'SUM()',
    avg: 'AVG()',
    average: 'AVG()',
    count: 'COUNT()',
    max: 'MAX()',
    maximum: 'MAX()',
    min: 'MIN()',
    minimum: 'MIN()',
    round: 'ROUND()',
    abs: 'ABS()',
    absolute: 'ABS()',
    ceil: 'CEIL()',
    floor: 'FLOOR()',
    sqrt: 'SQRT()'
  };

  /**
   * Group variables via the rule-editor package selectors so the section
   * order is data-driven ( per `expose.kind` ) and within-section sort
   * uses `kindAwareComparator`. The "Items en esta publicación" kind
   * defaults to alphabetical-by-label here; a kit-position comparator
   * will plug into the same seam in a follow-up ticket.
   */
  const variableGroups = useMemo(() => {
    const flat = flattenVariablesRecord( variables as Record<string, readonly MaterializedVariable[]> );
    const comparator: VariableComparator = kindAwareComparator({});
    return groupVariablesByKind( flat, comparator );
  }, [variables]);

  /**
   * Map<itemId, MaterializedVariable[]> built from the materialized chips
   * — used to render a friendly `brand model (sku)` label in the missing-
   * items banner. When the chip is gone from `variables`, falls back to
   * the raw itemId.
   */
  const variableByItemId = useMemo(() => {
    const byId = new Map<string, MaterializedVariable>();
    for ( const group of variableGroups ) {
      for ( const v of group.variables ) {
        const match = /itemId:'([^']+)'/.exec( v.value );
        if ( match && !byId.has( match[ 1 ])) {
          byId.set( match[ 1 ], v );
        }
      }
    }
    return byId;
  }, [variableGroups]);

  // Convert variables to suggestions
  const getVariableSuggestions = (): Suggestion[] => {
    const variableSuggestions: Suggestion[] = [];

    for ( const group of variableGroups ) {
      for ( const variable of group.variables ) {
        variableSuggestions.push({
          value: variable.label,
          label: variable.label,
          description: variable.description,
          type: 'variable' as const,
          group: variable.kind ?? group.kind,
          originalValue: variable.value
        });
      }
    }

    return variableSuggestions;
  };

  const getAllSuggestions = (): Suggestion[] => {
    return [
      ...getVariableSuggestions(),
      ...functions,
      ...operators
    ];
  };

  const convertStoredToDisplay = ( storedValue: string ): string => {
    let result = storedValue;

    const valueToLabelMap: Record<string, string> = {};
    Object.values( variables ).flat()
.forEach( option => {
      if ( option && option.value && option.label ) {
        valueToLabelMap[option.value] = option.label;
      }
    });

    const markers: {[key: string]: string } = {};
    let markerCount = 0;

    result = result.replace( /\{\{([^}]+)\}\}/g, ( match, capturedValue ) => {
      const label = valueToLabelMap[capturedValue];
      if ( label ) {
        const marker = `__FORMULA_DISPLAY_MARKER_${markerCount++}__`;
        markers[marker] = label;
        return marker;
      }
      return match;
    });

    for ( const [ marker, label ] of Object.entries( markers )) {
      result = result.replace( marker, label );
    }

    return result;
  };

  const convertDisplayToStored = ( displayValue: string ): string => {
    let result = displayValue;

    const labelToValueMap: Record<string, string> = {};
    Object.values( variables ).flat()
.forEach( option => {
      if ( option && option.value && option.label ) {
        labelToValueMap[option.label] = option.value;
      }
    });

    const sortedLabels = Object.keys( labelToValueMap ).sort(( a, b ) => b.length - a.length );

    const markers: {[key: string]: string } = {};
    let markerCount = 0;

    for ( const label of sortedLabels ) {
      const value = labelToValueMap[label];
      const marker = `__FORMULA_MARKER_${markerCount++}__`;

      const regex = new RegExp( `\\b${label.replace( /[.*+?^${}()|[\]\\]/g, '\\$&' )}\\b`, 'g' );
      result = result.replace( regex, marker );

      markers[marker] = `{{${value}}}`;
    }

    for ( const [ marker, replacement ] of Object.entries( markers )) {
      result = result.replace( marker, replacement );
    }

    return result;
  };

  useEffect(() => {
    setDisplayValue( convertStoredToDisplay( value ));
  }, [ value, variables ]);

  /**
   * Set of itemIds referenced by the current formula whose item is no
   * longer in the kit. Empty when no kit context was provided.
   *
   * Recomputed on every formula edit and on every kit change — so the
   * warning clears as soon as the user removes the offending chip OR the
   * item is re-added to the kit ( acceptance criterion 5 ).
   */
  const missingRefs = useMemo(() => {
    if ( !currentKitItemIds ) return new Set<string>();
    return missingItemRefs( formulaItemRefs( value ), currentKitItemIds );
  }, [ value, currentKitItemIds ]);

  const filterSuggestions = ( query: string ): Suggestion[] => {
    if ( !query ) return getAllSuggestions();

    const lowercaseQuery = query.toLowerCase();
    const allSuggestions = getAllSuggestions();

    if ( functionMappings[lowercaseQuery]) {
      const functionSuggestion = functions.find( f => f.value === functionMappings[lowercaseQuery]);
      if ( functionSuggestion ) {
        return [ functionSuggestion, ...allSuggestions.filter( s => s !== functionSuggestion ) ];
      }
    }

    return allSuggestions.filter( suggestion =>
      suggestion.value.toLowerCase().includes( lowercaseQuery ) ||
      suggestion.label.toLowerCase().includes( lowercaseQuery ) ||
      ( suggestion.group && suggestion.group.toLowerCase().includes( lowercaseQuery )));
  };

  const handleKeyDown = ( event: KeyboardEvent<HTMLInputElement> ) => {
    // handle ctrlspace for manual suggestion
    if ( event.ctrlKey && event.key === ' ' ) {
      event.preventDefault();
      const allSuggestions = getAllSuggestions();
      setSuggestions( allSuggestions );
      setSelectedSuggestionIndex( 0 );
      setShowSuggestions( true );
      return;
    }

    if ( showSuggestions ) {
      switch ( event.key ) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedSuggestionIndex( prev =>
            prev < suggestions.length - 1 ? prev + 1 : 0 );
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedSuggestionIndex( prev =>
            prev > 0 ? prev - 1 : suggestions.length - 1 );
          break;
        case 'Enter':
        case 'Tab':
          event.preventDefault();
          if ( suggestions[selectedSuggestionIndex]) {
            insertSuggestion( suggestions[selectedSuggestionIndex]);
          }
          break;
        case 'Escape':
          setShowSuggestions( false );
          break;
      }
    }
  };

  const getCurrentWord = ( text: string, position: number ): string => {
    const beforeCursor = text.substring( 0, position );

     
    const wordBoundaries = /[\s(),+\-*/=<>!&|]/;

    let wordStart = position;
    for ( let i = position - 1; i >= 0; i-- ) {
      if ( wordBoundaries.test( text[i])) {
        wordStart = i + 1;
        break;
      }
      if ( i === 0 ) {
        wordStart = 0;
      }
    }

    let wordEnd = position;
    for ( let i = position; i < text.length; i++ ) {
      if ( wordBoundaries.test( text[i])) {
        wordEnd = i;
        break;
      }
      if ( i === text.length - 1 ) {
        wordEnd = text.length;
      }
    }

    const currentWord = text.substring( wordStart, wordEnd );
    return currentWord.trim();
  };

  const insertSuggestion = ( suggestion: Suggestion ) => {
    const input = inputRef.current;
    if ( !input ) return;

    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;

     
    const wordBoundaries = /[\s(),+\-*/=<>!&|]/;

    let wordStart = start;
    for ( let i = start - 1; i >= 0; i-- ) {
      if ( wordBoundaries.test( displayValue[i])) {
        wordStart = i + 1;
        break;
      }
      if ( i === 0 ) {
        wordStart = 0;
      }
    }

    let wordEnd = end;
    for ( let i = end; i < displayValue.length; i++ ) {
      if ( wordBoundaries.test( displayValue[i])) {
        wordEnd = i;
        break;
      }
      if ( i === displayValue.length - 1 ) {
        wordEnd = displayValue.length;
      }
    }

    let insertValue = suggestion.value;
    if ( suggestion.type === 'variable' && suggestion.originalValue ) {
      insertValue = suggestion.label;
    }

    const newDisplayValue =
      displayValue.substring( 0, wordStart ) +
      insertValue +
      displayValue.substring( wordEnd );

    const newStoredValue = convertDisplayToStored( newDisplayValue );
    onChange( newStoredValue );

    if ( onValidate ) {
      const validationError = validateFormula( newStoredValue );
      onValidate( validationError || '' );
    }

    setShowSuggestions( false );

    setTimeout(() => {
      let newPosition = wordStart + insertValue.length;
      if ( suggestion.type === 'function' && suggestion.value.endsWith( '()' )) {
        newPosition = wordStart + insertValue.length - 1; // Inside the parentheses
      }
      input.setSelectionRange( newPosition, newPosition );
      input.focus();
    }, 0 );
  };

  const handleInputChange = ( event: React.ChangeEvent<HTMLInputElement> ) => {
    const newDisplayValue = event.currentTarget.value;
    setDisplayValue( newDisplayValue );

    const newStoredValue = convertDisplayToStored( newDisplayValue );
    onChange( newStoredValue );

     if ( onValidate ) {
       const validationError = validateFormula( newStoredValue );
       onValidate( validationError || '' );
     }

     
    const words = newDisplayValue.split( /[\s(),+\-*/=<>!&|]/ );
    const lastWord = words[words.length - 1]?.toLowerCase();
    if ( lastWord && functionMappings[lastWord]) {
      const cursorPos = event.currentTarget.selectionStart || 0;
      const beforeCursor = newDisplayValue.substring( 0, cursorPos );
      const afterCursor = newDisplayValue.substring( cursorPos );
      const lastWordStart = beforeCursor.lastIndexOf( lastWord );

      if ( lastWordStart >= 0 ) {
        const newValue = beforeCursor.substring( 0, lastWordStart ) + functionMappings[lastWord] + afterCursor;
         setDisplayValue( newValue );
         const newStoredValueWithFunction = convertDisplayToStored( newValue );
         onChange( newStoredValueWithFunction );

         if ( onValidate ) {
           const validationError = validateFormula( newStoredValueWithFunction );
           onValidate( validationError || '' );
         }

         setTimeout(() => {
           const newCursorPos = lastWordStart + functionMappings[lastWord].length - 1;
           event.currentTarget.setSelectionRange( newCursorPos, newCursorPos );
         }, 0 );
         return;
      }
    }

    const cursorPos = event.currentTarget.selectionStart || 0;
    const currentWord = getCurrentWord( newDisplayValue, cursorPos );

    if ( currentWord.length > 0 ) {
      const filteredSuggestions = filterSuggestions( currentWord );
      setSuggestions( filteredSuggestions );
      setSelectedSuggestionIndex( 0 );
      setShowSuggestions( filteredSuggestions.length > 0 );
    } else {
      setShowSuggestions( false );
    }
  };

  const handleFocus = () => {
    const allSuggestions = getAllSuggestions();
    if ( allSuggestions.length > 0 ) {
      setSuggestions( allSuggestions );
      setSelectedSuggestionIndex( 0 );
      setShowSuggestions( true );
    }
  };

  const getSuggestionColor = ( type: string ) => {
    switch ( type ) {
      case 'variable': return 'blue';
      case 'function': return 'green';
      case 'operator': return 'orange';
      default: return 'gray';
    }
  };

  const groupedSuggestions = suggestions.reduce(( groups, suggestion ) => {
    const key = suggestion.group || suggestion.type;
    if ( !groups[key]) {
      groups[key] = [];
    }
    groups[key].push( suggestion );
    return groups;
  }, {} as Record<string, Suggestion[]> );

  const validateFormula = ( storedFormula: string ): string | null => {
    if ( !storedFormula.trim()) return null;

    const potentialStoredFormula = convertDisplayToStored( storedFormula );
    const formulaToValidate = potentialStoredFormula !== storedFormula ? potentialStoredFormula : storedFormula;

     
    const allowedCharsRegex = /^[a-zA-Z0-9_\s{}()+\-*/.,:']+$/;
    if ( !allowedCharsRegex.test( formulaToValidate )) {
      return t( 'validation_invalid_characters', 'La fórmula contiene caracteres inválidos. Solo se permiten letras, números, espacios, llaves {{}}, paréntesis (), operadores (+, -, *, /), comas, dos puntos (:) y comillas simples (\')' );
    }
    const openBraces = ( formulaToValidate.match( /\{\{/g ) || []).length;
    const closeBraces = ( formulaToValidate.match( /\}\}/g ) || []).length;
    if ( openBraces !== closeBraces ) {
      return t( 'validation_unmatched_braces', 'Llaves desbalanceadas en la fórmula' );
    }

    let parenCount = 0;
    for ( const char of formulaToValidate ) {
      if ( char === '(' ) parenCount++;
      if ( char === ')' ) parenCount--;
      if ( parenCount < 0 ) return t( 'validation_unmatched_parentheses', 'Paréntesis desbalanceados' );
    }
    if ( parenCount !== 0 ) return t( 'validation_unmatched_parentheses', 'Paréntesis desbalanceados' );

    const validVariables = new Set<string>();
    Object.values( variables ).flat()
.forEach( option => {
      if ( option && option.value ) {
        validVariables.add( option.value );
      }
    });

    const validFunctions = [ 'SUM', 'AVG', 'COUNT', 'MAX', 'MIN', 'ROUND', 'ABS', 'CEIL', 'FLOOR', 'SQRT' ];
    const validOperators = [ '+', '-', '*', '/' ];

    const tokens = formulaToValidate.match( /\{\{[^}]*\}\}|[A-Za-z_][A-Za-z0-9_]*\s*\(|[+\-*/(),]|\d+(?:\.\d+)?|\S/g ) || [];

    let expectOperand = true;
    let functionDepth = 0;
    let functionArgCount: number[] = [];

    for ( let i = 0; i < tokens.length; i++ ) {
      const token = tokens[i].trim();
      if ( !token ) continue;

      if ( token.match( /^\s+$/ )) continue;

      if ( token.startsWith( '{{' ) && token.endsWith( '}}' )) {
        const variableValue = token.slice( 2, -2 );
        if ( !validVariables.has( variableValue )) {
          return t( 'validation_invalid_variable', { variable: token });
        }
        if ( !expectOperand ) {
          return t( 'validation_unexpected_variable', `Variable inesperada: ${token}` );
        }
        expectOperand = false;
        if ( functionDepth > 0 ) {
          functionArgCount[functionArgCount.length - 1]++;
        }
        continue;
      }

      if ( token.match( /^[A-Za-z_][A-Za-z0-9_]*\s*\($/ )) {
        const funcName = token.replace( /\s*\($/, '' );
        if ( !validFunctions.includes( funcName )) {
          return t( 'validation_invalid_function', `Función inválida: ${funcName}. Las funciones válidas son: ${validFunctions.join( ', ' )}` );
        }
        if ( !expectOperand ) {
          return t( 'validation_unexpected_function', `Función inesperada: ${funcName}` );
        }
        functionDepth++;
        functionArgCount.push( 0 );
        expectOperand = true;
        continue;
      }

      // Number
      if ( token.match( /^\d+(?:\.\d+)?$/ )) {
        if ( !expectOperand ) {
          return t( 'validation_unexpected_number', `Número inesperado: ${token}` );
        }
        expectOperand = false;
        if ( functionDepth > 0 ) {
          functionArgCount[functionArgCount.length - 1]++;
        }
        continue;
      }

      // Comma (function argument separator)
      if ( token === ',' ) {
        if ( functionDepth === 0 ) {
          return t( 'validation_comma_outside_function', 'Las comas solo pueden usarse dentro de funciones' );
        }
        if ( expectOperand ) {
          return t( 'validation_unexpected_comma', 'Coma inesperada (se esperaba operando)' );
        }
        expectOperand = true;
        continue;
      }

      // Operator
      if ( validOperators.includes( token )) {
        if ( expectOperand ) {
          return t( 'validation_unexpected_operator', `Operador inesperado: ${token}` );
        }
        expectOperand = true;
        continue;
      }

      // Opening parenthesis (not part of function)
      if ( token === '(' ) {
        if ( !expectOperand ) {
          return t( 'validation_unexpected_opening_paren', 'Paréntesis de apertura inesperado' );
        }
        expectOperand = true;
        continue;
      }

      // Closing parenthesis
      if ( token === ')' ) {
        if ( expectOperand && functionDepth === 0 ) {
          return t( 'validation_unexpected_closing_paren', 'Paréntesis de cierre inesperado' );
        }

        if ( functionDepth > 0 ) {
          const argCount = functionArgCount.pop() || 0;
          if ( argCount < 2 ) {
            return t( 'validation_function_min_args', 'Las funciones requieren al menos 2 argumentos (ej: MAX(valor1, valor2))' );
          }
          functionDepth--;
        }
        expectOperand = false;
        continue;
      }

      // Any other invalid token
      return t( 'validation_invalid_token', `Token inválido: '${token}'` );
    }

    // Check if formula ends properly
    if ( expectOperand ) {
      return t( 'validation_formula_ends_unexpectedly', 'La fórmula termina inesperadamente (se esperaba operando)' );
    }

    if ( functionDepth > 0 ) {
      return t( 'validation_unclosed_functions', 'Llamadas de función sin cerrar' );
    }

    return null;
  };

  const validationError = validateFormula( value );
  const displayError = error || validationError;

  const missingItemTooltip = tAutomation(
    'automation_rule_editor_formula_missing_item_tooltip',
    'Item no longer in this publication\'s kit; quantity will resolve to 0.'
  );

  return (
    <Stack gap={4}>
      <Popover
        opened={showSuggestions}
        onClose={() => setShowSuggestions( false )}
        position="bottom-start"
        width="target"
        shadow="md"
      >
        <Popover.Target>
          <TextInput
            ref={inputRef}
            label={label}
            value={displayValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={() => setTimeout(() => setShowSuggestions( false ), 150 )}
            placeholder={placeholder || t( 'formula_input_placeholder', 'Ingresar fórmula (Ctrl+Espacio para sugerencias)' )}
            required={required}
            radius="xs"
            leftSection={<IconFunction size={16} />}
            description={t( 'formula_input_description', 'Ctrl+Espacio para sugerencias' )}
            error={displayError}
          />
        </Popover.Target>

        <Popover.Dropdown p={0}>
          <ScrollArea h={200} scrollbars="y" type="scroll" scrollHideDelay={0}>
            <Stack gap={0}>
              {Object.entries( groupedSuggestions ).map(([ groupName, groupSuggestions ]) => (
                <div key={groupName}>
                  <Text
                    size="xs"
                    fw={500}
                    p="xs"
                    style={{ backgroundColor: 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-5))' }}
                    c="dimmed"
                    data-testid={componentsTestIds.ruleEditorPaletteSection( kebabCase( groupName ))}
                  >
                    {t( `suggestion_group_${groupName?.toLowerCase()}`, groupName.charAt( 0 ).toUpperCase() + groupName.slice( 1 ))}
                  </Text>
                  {groupSuggestions.map(( suggestion, index ) => {
                    const globalIndex = suggestions.indexOf( suggestion );
                    const chipTestId = suggestion.type === 'variable'
                      ? componentsTestIds.ruleEditorPaletteChip( buildChipSlug( suggestion ))
                      : undefined;
                    return (
                      <Group
                        key={`${groupName}-${index}`}
                        p="xs"
                        gap="xs"
                        data-testid={chipTestId}
                        style={{
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease',
                          backgroundColor: globalIndex === selectedSuggestionIndex
                            ? 'light-dark(var(--mantine-color-blue-1), rgba(34, 139, 230, 0.15))'
                            : undefined
                        }}
                        onClick={() => insertSuggestion( suggestion )}
                        onMouseEnter={( e ) => {
                          if ( globalIndex !== selectedSuggestionIndex ) {
                            e.currentTarget.style.backgroundColor = 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-5))';
                          }
                        }}
                        onMouseLeave={( e ) => {
                          if ( globalIndex !== selectedSuggestionIndex ) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <Badge
                          size="xs"
                          color={getSuggestionColor( suggestion.type )}
                          variant="light"
                        >
                          {suggestion.type}
                        </Badge>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <Text size="sm" fw={500} truncate>
                            {suggestion.label}
                          </Text>
                          {suggestion.description && (
                            <Text size="xs" c="dimmed" lineClamp={2}>
                              {suggestion.description}
                            </Text>
                          )}
                        </div>
                      </Group>
                    );
                  })}
                </div>
              ))}
            </Stack>
          </ScrollArea>
        </Popover.Dropdown>
      </Popover>

      {missingRefs.size > 0 && (
        <Group gap={6} pl="xs">
          {Array.from( missingRefs ).map(( itemId ) => {
            const variable = variableByItemId.get( itemId );
            const tooltipLabel = variable
              ? `${missingItemTooltip} (${variable.label})`
              : `${missingItemTooltip} (${itemId})`;
            return (
              <Tooltip key={itemId} label={tooltipLabel} withArrow position="top">
                <span
                  data-testid={componentsTestIds.ruleEditorFormulaWarningMissingItem( itemId )}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: 'light-dark(var(--mantine-color-yellow-7), var(--mantine-color-yellow-3))'
                  }}
                >
                  <IconAlertTriangle size={16} />
                </span>
              </Tooltip>
            );
          })}
        </Group>
      )}
    </Stack>
  );
};
